import { Component } from 'react';
import { getEleTop, querySelector, scrollToSmooth, strTrim, isEmpty } from '@/utils/index';
import { ProductTracker } from '@/utils/Tracker';
import dynamic from 'next/dynamic';
import OverviewConfig from './data.config';
import ProductCompareConfig from './components/Compare/data.config';

const OverView = dynamic(import('./components/OverView/Default'));
const OverViewModular = dynamic(import('./components/OverView/DefaultModular'));
const Description = dynamic(import('./components/Description'));
const Videos = dynamic(import('./components/Videos'));
const ImportantNote = dynamic(import('./components/ImportantNote'));
const Recommends = dynamic(import('./components/Recommends'));
const Faq = dynamic(import('./components/Faq'));
const WhatInTheBox = dynamic(import('./components/WhatInTheBox'));
const Compare = dynamic(import('./components/Compare'));
const AccessoryTypeDiff = dynamic(import('../ProductDiff/AccessoryTypeDiff'));
const AcessoryCompatiblity = dynamic(import('./components/AcessoryCompatiblity'));
const AcessoryCompatiblityMob = dynamic(import('./components/AcessoryCompatiblity/index.mobile'));
const ProductParameter = dynamic(import('./components/ProductParameter'));
const Comments = dynamic(import('./components/Comments'));
const CommentsMob = dynamic(import('./components/Comments/index.mobile'));

class BaseSection extends Component {
  getMenus = ({ cb }) => {
    const { LANG, country } = this.props.area;
    const {
      curProductCommodityRecommend,
      curSelectCommodityId,
      productDetail,
      packingListPoints = {},
      productReviewInfo,
      productParameterInfos = [],
      productImportantNotes = [],
    } = this.props.product;
    const { id: pid, videos, faqs, info, productCategory = {}, isNeedHidePrice } = productDetail;
    const { isMobile } = this.props.app;
    const hasCustomSpecs = OverviewConfig.customizeProduct.includes(pid);
    const showSpecs = hasCustomSpecs || !OverviewConfig.notShowSpec.includes(curSelectCommodityId);
    const showDesc = OverviewConfig.descProduct.includes(pid);
    const showCompare = OverviewConfig.compareProduct.includes(pid);
    const showPackage = !OverviewConfig.notShowPackage.includes(curSelectCommodityId);
    const showNote = !OverviewConfig.notShowNote.includes(curSelectCommodityId);
    const showRecommend = !isNeedHidePrice;

    const cProps = {
      pid,
      cid: curSelectCommodityId,
    };
    const overviewMenu = OverviewConfig.getMenu({ isMobile, LANG, productDetail })
      .map((o) => {
        if (o.desc === '描述') {
          // 只有部分机型才有
          if (showDesc) {
            o.visible = true;
            o.Component = <Description {...cProps} />;
          }
        } else if (o.desc === '产品详情') {
          const overviewConfig = this.getOverview();
          if (overviewConfig) {
            o = { ...this.getOverview(), ...o };
            o.Component = <o.component {...cProps} {...o} />;
          } else {
            o.visible = false;
          }
        } else if (o.desc === '技术参数' && showSpecs) {
          // 如果存在自定义配置，则优先自定义配置，否则使用后台配置。
          if (hasCustomSpecs) {
            o.visible = true;
          } else if (!isEmpty(productParameterInfos)) {
            o.visible = true;
            o.titleVisible = false;
            o.Component = <ProductParameter {...cProps} />;
          }
        } else if (o.desc === '包装清单' && showPackage) {
          if (!isEmpty(packingListPoints?.accessoryDiffData)) {
            // 配件类型包装清单
            o.visible = true;
            o.titleVisible = false;
            o.Component = <AccessoryTypeDiff {...cProps} />;
          } else if (strTrim(info.packing_list)) {
            o.visible = true;
            o.Component = <WhatInTheBox />;
          }
        } else if (o.desc === '宣传视频') {
          o.visible = videos && videos.filter((o) => o.type === 'relate_video').length > 0;
          o.Component = o.visible ? <Videos {...cProps} /> : null;
        } else if (o.desc === '版本对比') {
          if (showCompare) {
            o.visible = true;
          } else {
            o.visible = ProductCompareConfig.getProductCompareConfig({ pid, LANG, country });
            o.Component = o.visible ? <Compare {...cProps} /> : null;
          }
        } else if (o.desc === '配件适配性') {
          o.visible = !isEmpty(productDetail.accessoryCompatibilities);
          o.Component = o.visible ? (
            isMobile ? (
              <AcessoryCompatiblityMob
                {...cProps}
                type={
                  ['CM_CAMERA', 'CM_HAND_STABILIZER', 'CM_WEBCAM'].includes(productCategory?.categoryKey)
                    ? 'camera'
                    : 'accessory'
                }
              />
            ) : (
              <AcessoryCompatiblity
                {...cProps}
                type={
                  ['CM_CAMERA', 'CM_HAND_STABILIZER', 'CM_WEBCAM'].includes(productCategory?.categoryKey)
                    ? 'camera'
                    : 'accessory'
                }
              />
            )
          ) : null;
        } else if (o.desc === '注意事项' && showNote) {
          if (!isEmpty(productImportantNotes.data)) {
            const titleVisible = productImportantNotes.type !== 2;
            o.visible = true;
            o.titleVisible = titleVisible;
            o.Component = <ImportantNote titleVisible={titleVisible} {...cProps} />;
          }
        } else if (o.desc === '评论模块') {
          o.visible = productReviewInfo?.reviewCountSum;
          o.Component = o.visible ? isMobile ? <CommentsMob {...cProps} /> : <Comments {...cProps} /> : null;
        } else if (o.desc === 'FAQ') {
          o.visible = faqs && faqs.length > 0;
          o.Component = o.visible ? <Faq {...cProps} /> : null;
        } else if (o.desc === '推荐配件') {
          if (showRecommend) {
            o.visible = curProductCommodityRecommend && curProductCommodityRecommend.length > 0;
            o.Component = o.visible ? <Recommends {...cProps} /> : null;
          }
        } else if (o.desc === 'PDF链接') {
          o.visible = o?.enabledP?.includes(pid);
          o.titleVisible = o?.enabledP?.includes(pid);
        }
        return o;
      })
      .filter((o) => o.visible);
    // 相机类型/配件类型菜单顺序不同
    // 默认选中菜单第一项
    if (overviewMenu.length) {
      overviewMenu[0].selected = true;
      // descName: 菜单项的desc、targetPositoin：需要将descName菜单项插入到的位置（0 开始）
      const insertionPosition = (descName, targetPositoin = 1) => {
        const index = overviewMenu.findIndex((o) => o.desc === descName);
        if (index === -1) return;
        overviewMenu.splice(targetPositoin, 0, overviewMenu[index]);
        overviewMenu.splice(index + 1, 1);
      };

      if (pid === 26) {
        // x4版本对比在overview和ai editing后面
        insertionPosition('版本对比', 1);
        const overviewDesc = overviewMenu.find((o) => o.desc === '产品详情');
        const specs = overviewMenu.findIndex((o) => o.desc === '技术参数');
        // x4mtc，mtb，ski 不需要参数项
        if (['x4_mtc', 'x4_mtb', 'x4_ski'].includes(overviewDesc?.type) && specs !== -1) {
          overviewMenu.splice(specs, 1);
        }
      }
      if (pid === 27) {
        // go3s版本对比在overview后面
        insertionPosition('版本对比', 1);
        insertionPosition('技术参数', 2);
      }

      if (pid === 29) {
        // link2版本对比在overview后面
        insertionPosition('版本对比', 1);
        insertionPosition('技术参数', 2);
      }

      if (pid === 19) {
        // x4版本对比在overview后面
        const overviewDesc = overviewMenu.find((o) => o.desc === '产品详情');
        const comareIndex = overviewMenu.findIndex((o) => o.desc === '版本对比');
        if (overviewDesc?.type === 'x3_dive_kit' && comareIndex !== -1) {
          overviewMenu.splice(comareIndex, 1);
        } else {
          insertionPosition('版本对比', !showDesc ? 1 : 2);
        }
      }
      if (pid === 28) {
        // flow pro版本对比在overview后面
        insertionPosition('版本对比', 1);
        insertionPosition('技术参数', 2);
      }
      if (pid === 30) {
        // AcePro2版本对比在overview后面
        insertionPosition('版本对比', 1);
        insertionPosition('技术参数', 2);
      }
      if (pid === 32) {
        // flow2 pro版本对比在overview后面
        insertionPosition('版本对比', 1);
        insertionPosition('技术参数', 2);
        insertionPosition('FAQ', 3);
      }

      if (pid === 33) {
        // x5 pro版本对比在overview后面
        insertionPosition('版本对比', 1);
        insertionPosition('技术参数', 2);
      }
    }

    cb && cb(overviewMenu);
  };

  getMenusExtra = ({ cb }) => {
    const {
      productDetail: { id: pid },
    } = this.props.product;
    const { LANG, country } = this.props.area;
    const { isMobile } = this.props.app;

    const tempMenus =
      OverviewConfig.getExtraMenusConfig({
        isMobile,
        LANG,
        pid,
        cid: null,
        countryCode: country.code,
        commoditiesList: this.props.product.productDetail.commodities,
        isTargetingResult: this.props.abtest?.isTargetingResult,
        isX4DivingOriginal: this.props.abtest?.isX4DivingOriginal,
      }) || null;
    let extraMenus = [];
    if (tempMenus) {
      tempMenus.forEach((o) => {
        if (o.type === 'default') {
          extraMenus.push(o);
        } else {
          if (!o.specialEnableCountry) {
            extraMenus.push(o);
          }
          if (o.specialEnableCountry) {
            if (o.specialEnableCountry.length > 0 && o.specialEnableCountry.includes(country.code)) {
              extraMenus.push(o);
            }
          }
        }
      });
    }
    if (extraMenus && extraMenus.length === 1 && extraMenus[0]['type'] === 'default') {
      extraMenus = [];
    }
    cb && cb(extraMenus);
  };

  getMenusSpecs = ({ cb }) => {
    const { overviewType } = this.props.product;
    const { extraMenus } = this.state;
    let specsMenus = null;
    if (extraMenus && extraMenus.length > 0) {
      const menu = extraMenus.filter((o) => o.type === overviewType)[0];
      if (menu && menu.specsTitle && menu.specsTitle.length > 0) {
        specsMenus = menu.specsTitle;
      }
    }
    cb && cb(specsMenus);
  };

  getOverview = () => {
    const {
      productDetail: { id: pid },
      productModularOverview,
      curSelectCommodityId,
      overviewType,
    } = this.props.product;
    const { LANG } = this.props.area;
    const { isMobile, pageCommonConfig } = this.props.app;

    // 开发配置的产品页优先级最高
    const extraMenus = OverviewConfig.getExtraMenusConfig({
      LANG,
      isMobile,
      pid,
      cid: curSelectCommodityId,
      commoditiesList: this.props.product.productDetail.commodities,
      isTargetingResult: this.props.abtest?.isTargetingResult,
      isX4DivingOriginal: this.props.abtest?.isX4DivingOriginal,
    });

    if (extraMenus) {
      const defaultConfig = extraMenus.filter((o) => o.type === 'default')[0];
      const extraConfig = extraMenus.filter((o) => o.type === overviewType)[0];
      return extraConfig || defaultConfig;
    }

    // 取模块详情或富文本详情
    const overviewTypeConfig = OverviewConfig.getOverviewType({
      cid: curSelectCommodityId,
      productModularOverview,
      pageCommonConfig,
    });

    // 配置了模块化产品详情
    if (overviewTypeConfig.isModular) {
      return {
        type: 'modular',
        productOverview: productModularOverview,
        component: OverViewModular,
      };
    }

    // 配置了模块化产品详情
    if (overviewTypeConfig.isDefault) {
      return {
        type: 'richText',
        component: OverView,
      };
    }
  };

  handleScrollToSection = ({ anchor }) => {
    querySelector('#' + anchor).then((ele) => {
      querySelector('#pProductOverviewMenu').then((menu) => {
        const top = getEleTop(ele) - (menu.clientHeight || 0) + 1;
        scrollToSmooth(top, 0, 'instant');
        setTimeout(() => {
          scrollToSmooth(top, 0, 'instant');
        }, 50);
      });
    });
  };

  // 点击切换套餐详情版本
  handleChangeOverviewType = ({ extraMenu }) => {
    this.handleScrollToSection({ anchor: 'a_overview' });
    this.props.product.setStateValue('overviewType', extraMenu.type);
    this.getMenus({
      cb: (overviewMenu) => {
        this.setState({ overviewMenu });

        ProductTracker.productPageSubMenu({
          action: extraMenu.trackerAction || extraMenu.type,
          productState: this.props.product,
        });
      },
    });
  };
}

export default BaseSection;
